import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Paper,
    Typography,
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Menu,
    MenuItem,
    ListItemIcon,
    Divider,
    Button,
    Stack,
} from '@mui/material';
import {
    Visibility,
    PictureAsPdfOutlined,
    Cancel as CancelIcon,
    OpenInNew,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import TablePagination from '../../global/TablePagination';
import OrdersTableRow from '../../orders/Table/OrdersTableRow';
import { OrdersTableColumns } from '../../orders/Table/OrdersTableColumns';
import InvoiceViewerDialog from '../../orders/Detail/InvoiceViewerDialog';
import { useGetCustomerOrders } from '../../../hooks/customers/useGetCustomerOrders';
import { useUpdateOrderStatus } from '../../../hooks/orders/useUpdateOrderStatus';

const TERMINAL_STATUSES = new Set(['cancelled', 'refunded', 'delivered']);

const CustomerOrdersTab = ({ customerId }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const navigate = useNavigate();

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [previewOrder, setPreviewOrder] = useState(null);

    const params = useMemo(() => ({ page, per_page: perPage }), [page, perPage]);
    const { orders, meta, isLoading } = useGetCustomerOrders(customerId, params);

    const { updateOrderStatusMutate, isUpdatingStatus } = useUpdateOrderStatus();

    const openMenu = (e, order) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
        setSelectedOrder(order);
    };
    const closeMenu = () => {
        setMenuAnchor(null);
        setSelectedOrder(null);
    };

    const goToOrder = () => {
        if (!selectedOrder) return;
        navigate(`/orders/${selectedOrder.id}`);
        closeMenu();
    };

    const previewInvoice = () => {
        if (!selectedOrder) return;
        setPreviewOrder(selectedOrder);
        closeMenu();
    };

    const cancelOrder = () => {
        if (!selectedOrder) return;
        const ok = window.confirm(
            isArabic ? 'هل أنت متأكد من إلغاء هذا الطلب؟' : 'Cancel this order?'
        );
        if (!ok) {
            closeMenu();
            return;
        }
        updateOrderStatusMutate({
            id: selectedOrder.id,
            data: { status: 'cancelled', comment: 'Cancelled from customer detail' },
        });
        closeMenu();
    };

    const cancellable =
        selectedOrder &&
        !TERMINAL_STATUSES.has(selectedOrder.status) &&
        selectedOrder.status !== 'shipped';

    const colSpan = OrdersTableColumns.length + 3;

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    p: 0,
                    borderRadius: 3,
                    border: (t) => `1px solid ${t.palette.divider}`,
                    overflow: 'hidden',
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        p: { xs: 2, md: 2.5 },
                        borderBottom: (t) => `1px solid ${t.palette.divider}`,
                    }}
                >
                    <Typography variant="h6" fontWeight={700}>
                        {isArabic ? 'طلبات العميل' : 'Customer Orders'}
                    </Typography>
                    {meta?.total > 0 && (
                        <Button
                            size="small"
                            endIcon={<OpenInNew sx={{ transform: isArabic ? 'rotate(180deg)' : 'none' }} />}
                            onClick={() => navigate(`/orders?user_id=${customerId}`)}
                        >
                            {isArabic ? 'عرض الكل في الطلبات' : 'View in Orders'}
                        </Button>
                    )}
                </Stack>

                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: (t) => (t.palette.mode === 'dark' ? '#0B1220' : '#E3F2FD') }}>
                                <TableCell padding="checkbox" />
                                <TableCell sx={{ fontWeight: 700, width: 60 }}>
                                    {isArabic ? 'م' : 'S/N'}
                                </TableCell>
                                {OrdersTableColumns.map((col) => (
                                    <TableCell
                                        key={col.id}
                                        align={col.align === 'start' ? (isArabic ? 'right' : 'left') : col.align}
                                        sx={{ fontWeight: 700 }}
                                    >
                                        {isArabic ? col.labelAr : col.labelEn}
                                    </TableCell>
                                ))}
                                <TableCell align="center" sx={{ fontWeight: 700, width: 100 }}>
                                    {isArabic ? 'إجراءات' : 'Actions'}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading && (
                                <TableRow>
                                    <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {isArabic ? 'جاري التحميل...' : 'Loading...'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!isLoading && orders.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {isArabic ? 'لا توجد طلبات' : 'No orders'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!isLoading && orders.map((o, idx) => (
                                <OrdersTableRow
                                    key={o.id}
                                    item={o}
                                    index={(page - 1) * perPage + idx}
                                    isSelected={false}
                                    onRowCheckboxClick={() => {}}
                                    onMenuOpen={openMenu}
                                    onPreviewInvoice={(item) => setPreviewOrder(item)}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ px: 2, py: 1.2, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
                    <TablePagination
                        page={meta?.current_page || page}
                        perPage={meta?.per_page || perPage}
                        total={meta?.total || 0}
                        onPageChange={setPage}
                        onPerPageChange={(v) => { setPerPage(v); setPage(1); }}
                        perPageOptions={[5, 10, 15, 25, 50]}
                    />
                </Box>
            </Paper>

            {/* Row actions menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: isArabic ? 'left' : 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: isArabic ? 'left' : 'right' }}
            >
                <MenuItem onClick={goToOrder}>
                    <ListItemIcon>
                        <Visibility fontSize="small" color="primary" />
                    </ListItemIcon>
                    {isArabic ? 'عرض تفاصيل الطلب' : 'View order'}
                </MenuItem>
                <MenuItem onClick={previewInvoice}>
                    <ListItemIcon>
                        <PictureAsPdfOutlined fontSize="small" />
                    </ListItemIcon>
                    {isArabic ? 'معاينة الفاتورة' : 'Preview invoice'}
                </MenuItem>
                {cancellable && (
                    <>
                        <Divider />
                        <MenuItem onClick={cancelOrder} disabled={isUpdatingStatus} sx={{ color: 'error.main' }}>
                            <ListItemIcon>
                                <CancelIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            {isArabic ? 'إلغاء الطلب' : 'Cancel order'}
                        </MenuItem>
                    </>
                )}
            </Menu>

            {/* Invoice preview slideover */}
            <InvoiceViewerDialog
                open={Boolean(previewOrder)}
                order={previewOrder}
                onClose={() => setPreviewOrder(null)}
            />
        </>
    );
};

export default CustomerOrdersTab;
